import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import {
  AllChannelsIcon,
  Button,
  ChannelIcon,
  Dialog,
  EmptyState,
  Flex,
  IconButton,
  PageHeader,
  PageLayout,
  PlusIcon,
  Sidebar,
  Skeleton,
  VisuallyHidden,
  Text,
  Tooltip,
  UserIcon,
  toast,
  useIsMobileScreenSize,
  useSidebar,
} from '@buffer-mono/popcorn'

import { usePublicTracking } from '~publish/hooks/usePublicTracking'
import {
  PublicSurface,
  PublicSurfaceState,
  PublicSignupTrigger,
  trackPublicCta,
  trackPublicCtaViewed,
} from '~publish/helpers/publicTracking'

import { CommunityEmptyState } from '~publish/components/CommunityEmptyState'
import { PublicCommunityPreview } from '~publish/pages/Community/PublicCommunityPreview'
import {
  InsightsEmptyState,
  InsightsSamplePreview,
} from '~publish/pages/Insights'
import { RootLayout } from '~publish/components/RootLayout'
import { SectionAvatar } from '~publish/components/SectionAvatar'
import { getProductAreaTitle } from '~publish/helpers/getProductAreaTitle'
import { injectMetaViewportTag } from '~publish/helpers/injectMetaViewportTag'
import { navigateTo } from '~publish/helpers/navigation'
import { getBrowserGuestDraftStore } from '~publish/helpers/guestDrafts'
import { getChannelName } from '~publish/helpers/channels'
import {
  GUEST_CONNECTABLE_SERVICES,
  getGuestConnectChannelPath,
} from '~publish/helpers/guestNetworks'
import { getGuestAccountUrl } from '~publish/helpers/guestDraftHandoff'
import type {
  GuestAccountDestination,
  GuestDraftSignupReturnPath,
} from '~publish/helpers/guestDraftHandoff'
import { getBrowserGuestUnfinishedDraftStore } from '~publish/helpers/guestUnfinishedDraft'
import type { GuestDraft, GuestDraftStore } from '~publish/helpers/guestDrafts'
import {
  getLoginUrlForRedirectPath,
  getSignupUrlForRedirectPath,
} from '~publish/helpers/publicAuthUrls'
import { useGuestDrafts } from '~publish/hooks/useGuestDrafts'
import { useGuestMediaAccess } from '~publish/hooks/useGuestMediaAccess'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'
import { usePreloadPublicPostTemplates } from '~publish/pages/Create/Templates/hooks/usePublicPostTemplates'
import { createTemplates, newPost } from '~publish/legacy/routes'
import { PublicSchedulePage } from '~publish/pages/AllChannels/PublicSchedulePage'
import { RecoveryDialog } from '~publish/components/Composer/components/RecoveryDialog'
import { PublicComposerPage } from '~publish/pages/Create/PublicComposerPage'
import { PublicTemplateLibraryPage } from '~publish/pages/Create/Templates/components/PublicTemplateLibraryPage/PublicTemplateLibraryPage'

import type { Service } from '../MainSidebar/components/ChannelsSection/useChannels'
import {
  GlobalButtonTrigger,
  useGlobalButtonCompact,
} from '../MainSidebar/components/GlobalButton'
import {
  BufferLogoIconLink,
  BufferLogoLink,
} from '../MainSidebar/components/Logo/BufferLogoLink'
import {
  MobileSidebarDrawerShell,
  MobileSidebarProvider,
  MobileTopbarShell,
  useMobileSidebar,
} from '../MainSidebar/components/MobileSidebar'
import {
  isCommunityPath,
  isCreatePath,
  isInsightsPath,
  MAIN_NAVIGATION_ITEMS,
  SidebarNavigationList,
} from '../MainSidebar/components/MainNavigation'
import type { SidebarNavigationItem } from '../MainSidebar/components/MainNavigation'
import {
  MainSidebarFooter,
  MainSidebarFrame,
  MainSidebarHeader,
  MainSidebarSkeleton,
} from '../MainSidebar/components/MainSidebarFrame'
import { SidebarFooterLayout } from '../MainSidebar/components/SidebarFooter'
import { PublishSidebarProvider } from '../MainSidebar/PublishSidebarProvider'

import channelsSectionStyles from '../MainSidebar/components/ChannelsSection/ChannelsSection.module.css'
import {
  getAuthenticatedPathnameFromPublicPath,
  getPublicRouteSection,
  normalizePathname,
  toPublicPublishPath,
} from './loggedOutRouteIntent'
import type { PublicRouteSection } from './loggedOutRouteIntent'
import { GuestSessionFrame } from './GuestSessionFrame'
import styles from './PublicPublishShell.module.css'

const PUBLIC_CREATE_TEMPLATES_PATH = toPublicPublishPath(createTemplates.route)
const PUBLIC_NEW_POST_PATH = toPublicPublishPath(newPost.route)
const PUBLIC_SCHEDULE_PATH = toPublicPublishPath('/schedule')
const PUBLIC_COMMUNITY_PATH = toPublicPublishPath('/community')
const PUBLIC_INSIGHTS_PATH = toPublicPublishPath('/insights')

export { getLoginUrlForRedirectPath }

function getSignupReturnPath(
  section: PublicRouteSection | null,
): GuestDraftSignupReturnPath {
  switch (section) {
    case 'community':
      return '/community'
    case 'templates':
      return '/create/templates'
    case 'insights':
    case 'analytics':
      return '/insights'
    default:
      return '/schedule'
  }
}

function getCurrentAuthenticatedPathWithSearch(location: {
  pathname: string
  search: string
  hash: string
}): string {
  const authenticatedPathname =
    getAuthenticatedPathnameFromPublicPath(location.pathname) ??
    location.pathname

  return `${authenticatedPathname}${location.search}${location.hash}`
}

const CHANNEL_STOREFRONT_PATH = '/settings/channels?channelStorefront=true'

type ContinueToAccount = (
  destination: GuestAccountDestination,
  fallbackPath: string,
  returnTo?: GuestDraftSignupReturnPath,
) => void

function getChannelStorefrontLoginUrl(): string {
  return getLoginUrlForRedirectPath(CHANNEL_STOREFRONT_PATH)
}

function PublicGlobalButton(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { isCompact, showCollapsedUI } = useGlobalButtonCompact()

  return (
    <Tooltip
      content="New Post"
      open={isCompact ? undefined : false}
      side="right"
    >
      <GlobalButtonTrigger
        isCompact={isCompact}
        showCollapsedUI={showCollapsedUI}
        label="New Post"
        compactLabel="New Post"
        onClick={(): void =>
          navigate(PUBLIC_NEW_POST_PATH, {
            state: {
              returnTo: `${location.pathname}${location.search}${location.hash}`,
            },
          })
        }
      />
    </Tooltip>
  )
}

function PublicMainNavigation(): JSX.Element {
  const location = useLocation()
  const activePathname =
    getAuthenticatedPathnameFromPublicPath(location.pathname) ??
    normalizePathname(location.pathname)

  const items: SidebarNavigationItem[] = [
    {
      key: 'create',
      ...MAIN_NAVIGATION_ITEMS.create,
      to: PUBLIC_CREATE_TEMPLATES_PATH,
      selected: isCreatePath(activePathname),
    },
    {
      key: 'publish',
      ...MAIN_NAVIGATION_ITEMS.publish,
      to: PUBLIC_SCHEDULE_PATH,
      selected:
        activePathname === '/all-channels' ||
        activePathname === '/calendar' ||
        activePathname.startsWith('/post') ||
        activePathname.startsWith('/schedule') ||
        activePathname.startsWith('/all-channels/calendar') ||
        activePathname.startsWith('/calendar/'),
    },
    {
      key: 'community',
      ...MAIN_NAVIGATION_ITEMS.community,
      to: PUBLIC_COMMUNITY_PATH,
      selected:
        isCommunityPath(activePathname) ||
        activePathname.startsWith('/comments'),
    },
    {
      key: 'insights',
      ...MAIN_NAVIGATION_ITEMS.insights,
      to: PUBLIC_INSIGHTS_PATH,
      selected: isInsightsPath(activePathname),
      end: true,
    },
  ]

  return <SidebarNavigationList items={items} />
}

function PublicChannelsSection({
  onConnectChannels,
}: {
  onConnectChannels: (service: Service) => void
}): JSX.Element {
  const { state } = useSidebar()

  return (
    <Sidebar.Group className={channelsSectionStyles.channelsSection}>
      <Sidebar.Separator className={channelsSectionStyles.separator} />
      <Sidebar.GroupLabel className={channelsSectionStyles.groupLabel}>
        <span className={channelsSectionStyles.channelTitleLabel}>
          Channels
        </span>
      </Sidebar.GroupLabel>
      <Sidebar.List
        aria-label="Connect channels"
        className={styles.connectChannelsList}
      >
        {GUEST_CONNECTABLE_SERVICES.map((service) => (
          <Sidebar.ListItem key={service}>
            <Tooltip
              content={`Connect ${getChannelName(service)}`}
              side="right"
              open={state === 'collapsed' ? undefined : false}
            >
              <Sidebar.Button
                className={styles.channelButton}
                prefix={
                  <span className={styles.channelSlot}>
                    <ChannelIcon type={service} color="subtle" />
                  </span>
                }
                aria-label={`Connect ${getChannelName(service)}`}
                onClick={(): void => onConnectChannels(service)}
              >
                {getChannelName(service)}
              </Sidebar.Button>
            </Tooltip>
          </Sidebar.ListItem>
        ))}
      </Sidebar.List>
    </Sidebar.Group>
  )
}

type PublicSignupProps = {
  onSignUp: () => void
  onConnectChannels: (service: Service) => void
}

function PublicSignupButton({
  onSignUp,
}: Pick<PublicSignupProps, 'onSignUp'>): JSX.Element {
  const { state } = useSidebar()

  if (state === 'collapsed') {
    return (
      <Tooltip content="Create account" side="right">
        <IconButton
          variant="secondary"
          size="medium"
          label="Create account"
          onClick={onSignUp}
        >
          <UserIcon />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Button
      className={styles.signupButton}
      variant="secondary"
      size="medium"
      onClick={onSignUp}
    >
      <UserIcon />
      Create account
    </Button>
  )
}

function PublicSidebarContent({
  onConnectChannels,
}: Pick<PublicSignupProps, 'onConnectChannels'>): JSX.Element {
  return (
    <>
      <PublicGlobalButton />
      <PublicMainNavigation />
      <PublicChannelsSection onConnectChannels={onConnectChannels} />
    </>
  )
}

function PublicDesktopSidebar({
  onSignUp,
  onConnectChannels,
}: PublicSignupProps): JSX.Element {
  const { state } = useSidebar()

  return (
    <MainSidebarFrame>
      <MainSidebarHeader
        logo={
          state === 'expanded' ? (
            <BufferLogoLink
              to={PUBLIC_SCHEDULE_PATH}
              ariaLabel="Go to schedule"
            />
          ) : (
            <BufferLogoIconLink
              to={PUBLIC_SCHEDULE_PATH}
              ariaLabel="Go to schedule"
            />
          )
        }
        globalButton={<PublicGlobalButton />}
        navigation={<PublicMainNavigation />}
      />
      <Sidebar.Content>
        <PublicChannelsSection onConnectChannels={onConnectChannels} />
      </Sidebar.Content>
      <MainSidebarFooter>
        <SidebarFooterLayout
          start={<PublicSignupButton onSignUp={onSignUp} />}
        />
      </MainSidebarFooter>
    </MainSidebarFrame>
  )
}

function PublicMobileSidebarContent({
  onSignUp,
  onConnectChannels,
}: PublicSignupProps): JSX.Element {
  const location = useLocation()
  const { navigationOpen, setNavigationOpen, closeAll } = useMobileSidebar()

  useEffect(() => {
    closeAll()
  }, [location.pathname, closeAll])

  return (
    <>
      <MobileTopbarShell
        navigationOpen={navigationOpen}
        onNavigationOpenChange={setNavigationOpen}
        logo={
          <BufferLogoLink
            to={PUBLIC_SCHEDULE_PATH}
            ariaLabel="Go to schedule"
          />
        }
      />
      <MobileSidebarDrawerShell
        open={navigationOpen}
        onOpenChange={setNavigationOpen}
        footer={<PublicSignupButton onSignUp={onSignUp} />}
      >
        <PublicSidebarContent onConnectChannels={onConnectChannels} />
      </MobileSidebarDrawerShell>
    </>
  )
}

function PublicMobileSidebar(props: PublicSignupProps): JSX.Element {
  return (
    <MobileSidebarProvider>
      <PublicMobileSidebarContent {...props} />
    </MobileSidebarProvider>
  )
}

function PublicSidebar(props: PublicSignupProps): JSX.Element {
  const isMobile = useIsMobileScreenSize()

  return (
    <PublishSidebarProvider>
      {isMobile ? (
        <PublicMobileSidebar {...props} />
      ) : (
        <PublicDesktopSidebar {...props} />
      )}
    </PublishSidebarProvider>
  )
}

function PublicTemplatePreload(): null {
  usePreloadPublicPostTemplates()
  return null
}

function PublicSidebarSkeleton(): JSX.Element {
  const isMobile = useIsMobileScreenSize()

  if (isMobile) {
    return (
      <Flex direction="column" gap="lg" className={styles.loadingSidebar}>
        <Text size="lg" weight="bold">
          Buffer
        </Text>
      </Flex>
    )
  }

  return (
    <PublishSidebarProvider>
      <MainSidebarSkeleton channelRows={GUEST_CONNECTABLE_SERVICES.length} />
    </PublishSidebarProvider>
  )
}

/** Keep guest actions unavailable until the session check resolves. */
export function PublicPublishLoading(): JSX.Element {
  useLayoutEffect(() => {
    injectMetaViewportTag()
  }, [])

  const location = useLocation()
  const routeSection = getPublicRouteSection(location.pathname, location.search)

  return (
    <>
      <VisuallyHidden as="output">Loading Publish…</VisuallyHidden>
      <div aria-hidden="true">
        <GuestSessionFrame inactive>
          <RootLayout className={styles.publicLayout} skipLink={null}>
            <RootLayout.Sidebar>
              <PublicSidebarSkeleton />
            </RootLayout.Sidebar>
            <RootLayout.Main className={styles.pageMain}>
              <Flex
                direction="column"
                gap="lg"
                className={styles.loadingContent}
              >
                <Skeleton width={200} height={32} />
                <Skeleton height={160} />
                <Skeleton height={160} />
              </Flex>
            </RootLayout.Main>
          </RootLayout>
        </GuestSessionFrame>
      </div>
      {routeSection === 'templates' ? <PublicTemplatePreload /> : null}
    </>
  )
}

function PublicCenteredEmptyState({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <div className={styles.emptyStateWrapper}>
      <EmptyState size="large" variant="primary" className={styles.emptyState}>
        {children}
      </EmptyState>
    </div>
  )
}

function PublicConnectChannelsEmptyState({
  onContinueToAccount,
}: {
  onContinueToAccount: ContinueToAccount
}): JSX.Element {
  return (
    <PageLayout>
      <PageHeader separator={false}>
        <PageHeader.Row>
          <PageHeader.TitleGroup>
            <PageHeader.TitleLeading>
              <SectionAvatar>
                <AllChannelsIcon size={24} />
              </SectionAvatar>
            </PageHeader.TitleLeading>
            <PageHeader.Title>Channels</PageHeader.Title>
          </PageHeader.TitleGroup>
        </PageHeader.Row>
      </PageHeader>
      <PageLayout.Container>
        <PublicCenteredEmptyState>
          <EmptyState.Icon>
            <AllChannelsIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>Connect your channels</EmptyState.Heading>
          <EmptyState.Description>
            Add your social channels to start planning, publishing, and
            analyzing content in Buffer.
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button
              as="a"
              href={getChannelStorefrontLoginUrl()}
              onClick={(event): void => {
                event.preventDefault()
                onContinueToAccount('login', CHANNEL_STOREFRONT_PATH)
              }}
              variant="primary"
              size="medium"
            >
              <PlusIcon />
              Connect a Channel
            </Button>
          </EmptyState.Actions>
        </PublicCenteredEmptyState>
      </PageLayout.Container>
    </PageLayout>
  )
}

function PrivateResourceHandoff({
  onContinueToAccount,
}: {
  onContinueToAccount: ContinueToAccount
}): JSX.Element {
  const location = useLocation()
  const privatePath = getCurrentAuthenticatedPathWithSearch(location)

  return (
    <PublicCenteredEmptyState>
      <EmptyState.Heading>Log in to continue</EmptyState.Heading>
      <EmptyState.Description>
        This Publish link points to account-specific content. Log in and we’ll
        take you back here.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button
          as="a"
          href={getLoginUrlForRedirectPath(privatePath)}
          onClick={(event): void => {
            event.preventDefault()
            onContinueToAccount('login', privatePath, privatePath)
          }}
          variant="primary"
          size="medium"
        >
          Log in to continue
        </Button>
      </EmptyState.Actions>
    </PublicCenteredEmptyState>
  )
}

function DraftLimitDialog({
  open,
  onViewDrafts,
  onCancel,
}: {
  open: boolean
  onViewDrafts: () => void
  onCancel: () => void
}): JSX.Element {
  useEffect(() => {
    if (open)
      trackPublicCtaViewed('publish-composer-draftLimitDialog-viewDrafts-1')
  }, [open])
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen): void => {
        if (!nextOpen) onCancel()
      }}
    >
      <Dialog.Content size="small">
        <Dialog.Header>
          <Dialog.Title>Draft limit reached</Dialog.Title>
          <Dialog.Description>
            You can save up to 10 drafts in this browser. Delete a draft before
            you create another.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Button
            variant="primary"
            onClick={(): void => {
              trackPublicCta('publish-composer-draftLimitDialog-viewDrafts-1')
              onViewDrafts()
            }}
          >
            View Drafts
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

function getSavedDraftId(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined
  const { savedDraftId } = state as { savedDraftId?: unknown }
  return typeof savedDraftId === 'string' ? savedDraftId : undefined
}

function PublicPublishSurface({
  store,
  drafts,
  mediaAccess,
}: {
  store: GuestDraftStore
  drafts: readonly GuestDraft[]
  mediaAccess: GuestMediaAccessController
}): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const savedDraftId = getSavedDraftId(location.state)
  const openComposer = (searchParams: URLSearchParams): void => {
    const { pathname, search, hash } = window.location
    const query = searchParams.size > 0 ? `?${searchParams}` : ''
    navigate(`${PUBLIC_NEW_POST_PATH}${query}`, {
      state: { returnTo: `${pathname}${search}${hash}` },
    })
  }

  return (
    <PublicSchedulePage
      drafts={drafts}
      savedDraftId={savedDraftId}
      store={store}
      onCreate={(scheduledAt, network): void => {
        const searchParams = new URLSearchParams(window.location.search)
        if (scheduledAt !== undefined) {
          searchParams.set('scheduledAt', scheduledAt.toString())
        }
        if (network !== undefined) searchParams.set('network', network)
        openComposer(searchParams)
      }}
      mediaAccess={mediaAccess}
      onEdit={(draftId): void => {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('guestDraftId', draftId)
        openComposer(searchParams)
      }}
    />
  )
}

type InitialComposerRoute = Readonly<{
  draft?: GuestDraft
  unfinishedDraft?: GuestDraft
  returnTo: string
  canOpenComposer: boolean
  hasUnknownDraft: boolean
}>

function getScheduleReturnPath(state: unknown): string {
  if (
    typeof state !== 'object' ||
    state === null ||
    !('returnTo' in state) ||
    typeof state.returnTo !== 'string'
  ) {
    return PUBLIC_SCHEDULE_PATH
  }

  const returnUrl = new URL(state.returnTo, window.location.origin)
  if (
    returnUrl.origin !== window.location.origin ||
    returnUrl.pathname !== PUBLIC_SCHEDULE_PATH
  ) {
    return PUBLIC_SCHEDULE_PATH
  }

  return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`
}

function PublicComposerRoute({
  store,
  canCreateDraft,
  mediaAccess,
}: {
  store: GuestDraftStore
  canCreateDraft: boolean
  mediaAccess: GuestMediaAccessController
}): JSX.Element | null {
  const location = useLocation()
  const navigate = useNavigate()
  const [initialRoute] = useState<InitialComposerRoute>(() => {
    const searchParams = new URLSearchParams(location.search)
    const requestedDraftId = searchParams.has('guestDraftId')
      ? (searchParams.get('guestDraftId') ?? '')
      : undefined
    const draft = requestedDraftId ? store.getById(requestedDraftId) : undefined
    const canOpenComposer =
      requestedDraftId === undefined ? canCreateDraft : draft !== undefined
    const isSeededComposer =
      requestedDraftId !== undefined || searchParams.has('templateId')
    const unfinishedDraftId =
      canOpenComposer && !isSeededComposer
        ? getBrowserGuestUnfinishedDraftStore().read()
        : undefined

    return {
      draft,
      unfinishedDraft: unfinishedDraftId
        ? store.getById(unfinishedDraftId)
        : undefined,
      returnTo: getScheduleReturnPath(location.state),
      canOpenComposer,
      hasUnknownDraft: requestedDraftId !== undefined && draft === undefined,
    }
  })
  const [unfinishedDraft, setUnfinishedDraft] = useState(
    initialRoute.unfinishedDraft,
  )
  const [resumedDraft, setResumedDraft] = useState<GuestDraft>()
  const isResumingRef = useRef(false)
  const savedDraftIdRef = useRef<string>()
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(
    !initialRoute.hasUnknownDraft && !initialRoute.canOpenComposer,
  )

  const viewDrafts = (): void => {
    navigate(`${PUBLIC_SCHEDULE_PATH}?tab=drafts`, { replace: true })
  }

  const dismissComposer = (): void => {
    navigate(initialRoute.returnTo, {
      replace: true,
      state: savedDraftIdRef.current
        ? { savedDraftId: savedDraftIdRef.current }
        : undefined,
    })
  }

  const resumeUnfinishedDraft = (): void => {
    if (!unfinishedDraft) return

    const searchParams = new URLSearchParams(location.search)
    searchParams.set('guestDraftId', unfinishedDraft.id)
    isResumingRef.current = true
    setResumedDraft(unfinishedDraft)
    setUnfinishedDraft(undefined)
    navigate(`${PUBLIC_NEW_POST_PATH}?${searchParams}`, {
      replace: true,
      state: location.state,
    })
  }

  const discardUnfinishedDraft = (): void => {
    if (isResumingRef.current) {
      isResumingRef.current = false
      return
    }
    if (!unfinishedDraft) return

    getBrowserGuestUnfinishedDraftStore().clear(unfinishedDraft.id)
    setUnfinishedDraft(undefined)
  }

  const cancelLimit = (): void => {
    if (initialRoute.canOpenComposer) {
      setIsLimitDialogOpen(false)
      return
    }

    dismissComposer()
  }

  useEffect(() => {
    if (initialRoute.hasUnknownDraft) {
      navigate(`${PUBLIC_SCHEDULE_PATH}?tab=drafts`, { replace: true })
    }
  }, [initialRoute.hasUnknownDraft, navigate])

  if (initialRoute.hasUnknownDraft) return null

  return (
    <>
      {initialRoute.canOpenComposer && unfinishedDraft === undefined ? (
        <PublicComposerPage
          store={store}
          initialDraft={resumedDraft ?? initialRoute.draft}
          mediaAccess={mediaAccess}
          onDraftIdChange={(draftId): void => {
            const searchParams = new URLSearchParams(location.search)
            searchParams.delete('guestDraftId')
            searchParams.delete('templateId')
            if (draftId) searchParams.set('guestDraftId', draftId)
            const search = searchParams.size > 0 ? `?${searchParams}` : ''
            navigate(`${PUBLIC_NEW_POST_PATH}${search}`, {
              replace: true,
              state: location.state,
            })
          }}
          onDismiss={dismissComposer}
          onDraftSaved={(draftId): void => {
            savedDraftIdRef.current = draftId
          }}
          onDraftRemoved={viewDrafts}
          onLimitReached={(): void => setIsLimitDialogOpen(true)}
        />
      ) : null}
      <RecoveryDialog
        open={unfinishedDraft !== undefined}
        onRestore={resumeUnfinishedDraft}
        onDiscard={discardUnfinishedDraft}
        previewText={unfinishedDraft?.text}
      />
      <DraftLimitDialog
        open={isLimitDialogOpen}
        onViewDrafts={viewDrafts}
        onCancel={cancelLimit}
      />
    </>
  )
}

/**
 * SECURITY: This shell renders without a Buffer session. Its children and hooks
 * must support anonymous visitors and must not expose private account data.
 * AuthGate selects this shell after a missing or expired session; it does not
 * protect its contents. See ./README.md before adding components or requests.
 */
export function PublicPublishShell({
  guestDraftStore,
}: {
  guestDraftStore?: GuestDraftStore
} = {}): JSX.Element {
  useLayoutEffect(() => {
    injectMetaViewportTag()
  }, [])

  const location = useLocation()
  const routeSection = getPublicRouteSection(location.pathname, location.search)
  const store = useMemo(
    () => guestDraftStore ?? getBrowserGuestDraftStore(),
    [guestDraftStore],
  )
  const guestDraftSnapshot = useGuestDrafts(store)
  const guestMediaAccess = useGuestMediaAccess()
  const signupReturnPath = getSignupReturnPath(routeSection)
  const trackingSurface =
    routeSection === 'templates'
      ? PublicSurface.CREATE
      : routeSection === 'insights' || routeSection === 'analytics'
        ? PublicSurface.INSIGHTS
        : routeSection === 'community'
          ? PublicSurface.COMMUNITY
          : routeSection === 'composer' || routeSection === 'publish'
            ? PublicSurface.PUBLISH
            : undefined
  const trackingState =
    routeSection === 'templates'
      ? PublicSurfaceState.TEMPLATES
      : routeSection === 'composer'
        ? PublicSurfaceState.COMPOSER
        : new URLSearchParams(location.search).get('preview') === 'true'
          ? PublicSurfaceState.SAMPLE_DATA
          : routeSection === 'publish' && guestDraftSnapshot.drafts.length > 0
            ? PublicSurfaceState.DRAFT_LIST
            : PublicSurfaceState.EMPTY_STATE
  const connectSurfaceCta =
    trackingSurface === PublicSurface.INSIGHTS ||
    trackingSurface === PublicSurface.COMMUNITY
      ? `publish-${trackingSurface}-${trackingSurface === PublicSurface.INSIGHTS && trackingState === PublicSurfaceState.SAMPLE_DATA ? 'sampleDataBanner' : 'emptyState'}-connectChannel-1`
      : undefined
  const visibleCtas = useMemo(
    () => [
      'publish-shell-nav-signUp-1',
      ...(connectSurfaceCta ? [connectSurfaceCta] : []),
    ],
    [connectSurfaceCta],
  )
  usePublicTracking(trackingSurface, trackingState, visibleCtas)

  usePageTitle(
    getProductAreaTitle(
      routeSection === 'analytics'
        ? '/insights'
        : (getAuthenticatedPathnameFromPublicPath(location.pathname) ?? '/'),
    ),
  )

  const continueToAccount: ContinueToAccount = (
    destination,
    fallbackPath,
    returnTo,
  ) => {
    const url = getGuestAccountUrl({
      destination,
      hasGuestDrafts: store.getSnapshot().drafts.length > 0,
      fallbackPath,
      returnTo,
    })
    if (!url) {
      toast.error(
        'Your drafts could not be prepared for your account. Please try again.',
      )
      return
    }
    navigateTo(url)
  }
  const signUp = (): void => {
    trackPublicCta('publish-shell-nav-signUp-1', PublicSignupTrigger.NAV_CTA)
    continueToAccount('signup', signupReturnPath)
  }
  const connectFromSurface = (): void => {
    if (connectSurfaceCta)
      trackPublicCta(connectSurfaceCta, PublicSignupTrigger.CONNECT_CHANNEL)
    continueToAccount('signup', signupReturnPath)
  }
  const connectChannels = (service: Service): void => {
    trackPublicCta(
      'publish-shell-channelList-connectChannel-1',
      PublicSignupTrigger.CONNECT_CHANNEL,
    )
    const connectChannelPath = getGuestConnectChannelPath(service)
    continueToAccount('signup', connectChannelPath, connectChannelPath)
  }

  if (routeSection === 'home') {
    return (
      <Navigate
        to={{
          pathname: PUBLIC_SCHEDULE_PATH,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    )
  }

  return (
    <GuestSessionFrame
      action={
        <Button
          variant="secondary"
          size="small"
          className={styles.guestSignUpButton}
          onClick={signUp}
        >
          Create account
        </Button>
      }
    >
      <RootLayout className={styles.publicLayout}>
        <RootLayout.Sidebar>
          <PublicSidebar
            onSignUp={signUp}
            onConnectChannels={connectChannels}
          />
        </RootLayout.Sidebar>
        <RootLayout.Main
          className={
            routeSection === 'templates' ||
            routeSection === 'community' ||
            routeSection === 'insights' ||
            routeSection === 'analytics' ||
            routeSection === 'connect-channels'
              ? styles.pageMain
              : styles.main
          }
        >
          {routeSection === 'composer' || routeSection === 'publish' ? (
            <>
              <PublicPublishSurface
                store={store}
                drafts={guestDraftSnapshot.drafts}
                mediaAccess={guestMediaAccess}
              />
              {routeSection === 'composer' ? (
                <PublicComposerRoute
                  store={store}
                  canCreateDraft={guestDraftSnapshot.canCreateDraft}
                  mediaAccess={guestMediaAccess}
                />
              ) : null}
            </>
          ) : routeSection === 'templates' ? (
            <PublicTemplateLibraryPage />
          ) : routeSection === 'community' ? (
            <PublicCommunityPreview>
              <CommunityEmptyState.Actions>
                <Button
                  as="a"
                  href={getSignupUrlForRedirectPath('/community')}
                  onClick={(event): void => {
                    event.preventDefault()
                    connectFromSurface()
                  }}
                  variant="primary"
                >
                  <PlusIcon />
                  Connect a Channel
                </Button>
              </CommunityEmptyState.Actions>
            </PublicCommunityPreview>
          ) : routeSection === 'insights' || routeSection === 'analytics' ? (
            <InsightsEmptyState>
              <InsightsEmptyState.Header />
              <InsightsEmptyState.Content>
                <InsightsSamplePreview>
                  <InsightsEmptyState.Banner>
                    <InsightsEmptyState.Actions>
                      <Button
                        as="a"
                        href={getSignupUrlForRedirectPath('/insights')}
                        onClick={(event): void => {
                          event.preventDefault()
                          connectFromSurface()
                        }}
                        variant="primary"
                      >
                        <PlusIcon />
                        Connect a Channel
                      </Button>
                    </InsightsEmptyState.Actions>
                  </InsightsEmptyState.Banner>
                  <InsightsSamplePreview.Toggle />
                  <InsightsSamplePreview.Content />
                </InsightsSamplePreview>
              </InsightsEmptyState.Content>
            </InsightsEmptyState>
          ) : routeSection === 'connect-channels' ? (
            <PublicConnectChannelsEmptyState
              onContinueToAccount={continueToAccount}
            />
          ) : (
            <PrivateResourceHandoff onContinueToAccount={continueToAccount} />
          )}
        </RootLayout.Main>
      </RootLayout>
    </GuestSessionFrame>
  )
}
